import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Upload, Download, Loader2, CheckCircle, AlertCircle, RotateCcw,
  Monitor, Video, X, FolderOpen,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useCredits, CREDIT_OPERATIONS } from '@/hooks/useCredits';
import { useCreditConfirm } from '@/components/credits/CreditConfirmDialog';
import { UpgradeModal } from './subscription';
import { LoginModal } from './LoginModal';
import { ToolShell, StepCard, StepHeader, type ToolStep } from './tool-shell';
import LibraryPicker, { probeVideoUrl, stagedFileName, type StagedLibraryFile } from './library/LibraryPicker';
import { useNavigate } from 'react-router-dom';

type WizardStep = 'upload' | 'slate' | 'process' | 'download';

const STEP_ORDER: WizardStep[] = ['upload', 'slate', 'process', 'download'];

const STEPS: ToolStep[] = [
  { key: 'upload', label: 'Upload', blurb: 'Drop in the tape that needs a slate.' },
  { key: 'slate', label: 'Slate', blurb: 'Name, role, agency — styled your way.' },
  { key: 'process', label: 'Render', blurb: 'Burn the slate into the video.' },
  { key: 'download', label: 'Download', blurb: 'Save the slated tape to your device.' },
];

interface VideoInfo {
  file: File;
  name: string;
  objectUrl: string;
  s3Key: string;
  s3Url: string;
  duration: number;
  width: number;
  height: number;
  status: 'uploading' | 'converting' | 'uploaded' | 'error';
  uploadProgress?: number;
}

interface NameSlateState {
  name: string;
  role: string;
  agency: string;
  font: string;
  position: 'center' | 'bottom';
  timing: 'black' | 'overlay';
}

const formatTime = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 10);
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms}`;
};

// Font URLs handed to Shotstack in `timeline.fonts`.
//
// These are self-hosted copies in hey-joe-videos/fonts/, NOT fonts.gstatic.com
// URLs. Google's gstatic paths embed a version segment (…/montserrat/v29/…) and
// Google rotates it — when Montserrat went v29 → v31 the pinned URL started
// 404ing, and Shotstack fails the WHOLE render if any referenced asset 404s.
// That silently took the name slate down for every user until someone
// complained. Six of the eight URLs here were dead by the time we caught it.
//
// Self-hosting removes Google from the render path entirely. If you add a font,
// upload the .ttf to that prefix rather than pasting a gstatic URL.
const FONT_URLS: Record<string, string> = {
  'Montserrat': 'https://plus.heyjoe.io/api/s3/fonts/montserrat.ttf',
  'Open Sans': 'https://plus.heyjoe.io/api/s3/fonts/open-sans.ttf',
  'Lato': 'https://plus.heyjoe.io/api/s3/fonts/lato.ttf',
  'Roboto': 'https://plus.heyjoe.io/api/s3/fonts/roboto.ttf',
  'Playfair Display': 'https://plus.heyjoe.io/api/s3/fonts/playfair-display.ttf',
  'Oswald': 'https://plus.heyjoe.io/api/s3/fonts/oswald.ttf',
  'Raleway': 'https://plus.heyjoe.io/api/s3/fonts/raleway.ttf',
  'Poppins': 'https://plus.heyjoe.io/api/s3/fonts/poppins.ttf',
};

// Small segmented control matching the design's Radio pattern (enhancer.jsx
// line 1082) — pill row with the active option getting a raised surface.
// Local to this file; not yet promoted to a shared primitive.
const SegmentedControl: React.FC<{
  options: { value: string; label: string }[];
  active: string;
  onChange: (v: string) => void;
}> = ({ options, active, onChange }) => (
  <div
    className="flex"
    style={{
      background: 'var(--hj-surface-2)',
      borderRadius: 'var(--hj-r-sm)',
      padding: 3,
      gap: 2,
      border: '1px solid var(--hj-hairline-soft)',
    }}
  >
    {options.map((o) => {
      const isActive = o.value === active;
      return (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className="flex-1 transition-colors"
          style={{
            padding: '8px 10px',
            background: isActive ? 'var(--hj-surface-3)' : 'transparent',
            border: 0,
            borderRadius: 'calc(var(--hj-r-sm) - 2px)',
            color: isActive ? 'var(--hj-ink)' : 'var(--hj-ink-3)',
            fontSize: 12.5,
            fontWeight: isActive ? 600 : 500,
            fontFamily: 'inherit',
            cursor: 'pointer',
            minHeight: 36,
          }}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

export const NameSlateTool = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getAuthHeaders, getAuthToken } = useAuth();
  const { isSubscriber, loading: subscriptionLoading } = useSubscription();
  const { checkCredits } = useCredits();
  const { confirmCredit, CreditConfirmDialog } = useCreditConfirm();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [completedSteps, setCompletedSteps] = useState<Set<WizardStep>>(new Set());

  // Upload state — single video for this tool
  const [video, setVideo] = useState<VideoInfo | null>(null);

  // Slate state — the tool IS the slate, so no enable toggle
  const [nameSlate, setNameSlate] = useState<NameSlateState>({
    name: '',
    role: '',
    agency: '',
    font: 'Montserrat',
    position: 'center',
    timing: 'black',
  });

  // Render state
  const [renderStatus, setRenderStatus] = useState<'idle' | 'rendering' | 'failed' | 'completed'>('idle');
  const [renderProgress, setRenderProgress] = useState(0);
  const [renderError, setRenderError] = useState('');
  const [finalVideoUrl, setFinalVideoUrl] = useState('');

  // Track paid credit operations so retries don't re-charge
  const [creditsPaidFor, setCreditsPaidFor] = useState<Set<string>>(new Set());

  const previewVideoRef = useRef<HTMLVideoElement>(null);

  // Load Google Fonts for the live slate preview
  useEffect(() => {
    const families = ['Montserrat', 'Open+Sans', 'Lato', 'Roboto', 'Playfair+Display', 'Oswald', 'Raleway', 'Poppins'];
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${families.map(f => `family=${f}`).join('&')}&display=swap`;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  // Credit confirmation helper
  const isCreditUser = useCallback(() => !!getAuthToken(), [getAuthToken]);

  const requireCreditConfirmation = useCallback(async (
    operationType: string,
    operationName: string,
    onConfirm: () => void
  ) => {
    // Skip credit check if already paid in this session
    if (creditsPaidFor.has(operationType)) { onConfirm(); return; }
    if (!isCreditUser()) { onConfirm(); return; }
    const creditCheck = await checkCredits(operationType);
    if (creditCheck?.isFree) { onConfirm(); return; }
    if (!subscriptionLoading && !isSubscriber) { setShowUpgradeModal(true); return; }
    if (creditCheck?.hasCredits) {
      confirmCredit(operationType as any, operationName, () => {
        setCreditsPaidFor(prev => new Set([...prev, operationType]));
        onConfirm();
      });
    } else {
      toast({ title: 'Not enough video tokens', description: 'Please purchase more video tokens to continue.', variant: 'destructive' });
    }
  }, [isCreditUser, checkCredits, isSubscriber, subscriptionLoading, confirmCredit, toast, creditsPaidFor]);

  // ===== NAVIGATION =====
  const stepIndex = STEP_ORDER.indexOf(currentStep);

  const goToStep = (step: WizardStep) => {
    const targetIndex = STEP_ORDER.indexOf(step);
    if (targetIndex > stepIndex && !completedSteps.has(STEP_ORDER[targetIndex - 1])) return;
    if (targetIndex < stepIndex) {
      // Going backwards invalidates the render — a new pass will re-render
      setRenderStatus('idle');
      setRenderProgress(0);
      setRenderError('');
      setFinalVideoUrl('');
      setCompletedSteps(prev => {
        const next = new Set(prev);
        next.delete('process');
        next.delete('download');
        return next;
      });
    }
    setCurrentStep(step);
  };

  const advanceStep = () => {
    const nextIndex = stepIndex + 1;
    if (nextIndex < STEP_ORDER.length) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(STEP_ORDER[nextIndex]);
    }
  };

  const resetAll = () => {
    if (video) URL.revokeObjectURL(video.objectUrl);
    setVideo(null);
    setNameSlate({ name: '', role: '', agency: '', font: 'Montserrat', position: 'center', timing: 'black' });
    setRenderStatus('idle');
    setRenderProgress(0);
    setRenderError('');
    setFinalVideoUrl('');
    setCompletedSteps(new Set());
    setCurrentStep('upload');
  };

  // ===== STEP 1: UPLOAD =====
  const uploadFileToS3 = async (file: File) => {
    try {
      const res = await fetch('/get-upload-url', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ fileName: file.name, fileType: file.type || 'video/mp4', ephemeral: true }),
      });
      if (!res.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, key, downloadUrl } = await res.json();

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setVideo(prev => prev ? { ...prev, uploadProgress: pct } : prev);
          }
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
      });

      return { key, downloadUrl };
    } catch (err: any) {
      setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
      toast({ title: 'Upload failed', description: `${file.name}: ${err.message}`, variant: 'destructive' });
      return null;
    }
  };

  // MOV → MP4 conversion via AWS MediaConvert (handles HDR→SDR tone mapping)
  const convertMovClip = async (s3Key: string) => {
    try {
      const res = await fetch('/api/convert-mov', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ s3Key }),
      });
      if (!res.ok) throw new Error('Failed to start conversion');
      const { jobId } = await res.json();
      pollConversion(jobId);
    } catch (err: any) {
      setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
      toast({ title: 'Conversion failed', description: err.message, variant: 'destructive' });
    }
  };

  const pollConversion = async (jobId: string) => {
    const poll = async () => {
      try {
        const res = await fetch(`/api/convert-mov/status/${jobId}`);
        if (!res.ok) throw new Error('Failed to check conversion status');
        const data = await res.json();

        if (data.status === 'completed') {
          // Load metadata from the converted MP4 (Chrome can read H.264)
          const tempVideo = document.createElement('video');
          tempVideo.preload = 'metadata';
          tempVideo.src = data.downloadUrl;
          let metadataHandled = false;
          const updateAsUploaded = (withMetadata: boolean) => {
            if (metadataHandled) return;
            metadataHandled = true;
            setVideo(prev => prev ? {
              ...prev,
              s3Key: data.outputKey,
              s3Url: data.downloadUrl,
              objectUrl: `/api/s3/hey-joe-video-rotator/${data.outputKey}`,
              ...(withMetadata ? {
                duration: tempVideo.duration,
                width: tempVideo.videoWidth,
                height: tempVideo.videoHeight,
              } : {}),
              status: 'uploaded' as const,
            } : prev);
          };
          tempVideo.onloadedmetadata = () => updateAsUploaded(true);
          tempVideo.onerror = () => updateAsUploaded(false);
          // Fallback: if neither event fires within 10s, update anyway
          setTimeout(() => updateAsUploaded(false), 10000);
        } else if (data.status === 'failed') {
          setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
          toast({ title: 'MOV conversion failed', description: data.error, variant: 'destructive' });
        } else {
          // Still processing — poll again in 5 seconds
          setTimeout(poll, 5000);
        }
      } catch (err: any) {
        setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
        toast({ title: 'Conversion error', description: err.message, variant: 'destructive' });
      }
    };
    poll();
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    // Require login + subscription before allowing uploads
    if (!getAuthToken()) {
      setShowLoginModal(true);
      return;
    }
    if (!subscriptionLoading && !isSubscriber) {
      setShowUpgradeModal(true);
      return;
    }

    const file = acceptedFiles.find(f =>
      f.type.startsWith('video/') || ['.mp4', '.mov', '.webm', '.mkv', '.avi'].some(ext => f.name.toLowerCase().endsWith(ext))
    );
    if (!file) {
      toast({ title: 'Invalid file', description: 'Please select a video file.', variant: 'destructive' });
      return;
    }

    // Replacing the source invalidates everything downstream
    setVideo(prev => { if (prev) URL.revokeObjectURL(prev.objectUrl); return prev; });
    setRenderStatus('idle');
    setRenderProgress(0);
    setRenderError('');
    setFinalVideoUrl('');
    setCompletedSteps(new Set());

    const newVideo: VideoInfo = {
      file,
      name: file.name,
      objectUrl: URL.createObjectURL(file),
      s3Key: '',
      s3Url: '',
      duration: 0,
      width: 0,
      height: 0,
      status: 'uploading',
    };
    setVideo(newVideo);

    // Capture duration + dimensions from the local file (needed for the
    // Shotstack clip length and output size)
    const tempVideo = document.createElement('video');
    tempVideo.preload = 'metadata';
    tempVideo.src = newVideo.objectUrl;
    tempVideo.onloadedmetadata = () => {
      setVideo(prev => prev ? { ...prev, duration: tempVideo.duration, width: tempVideo.videoWidth, height: tempVideo.videoHeight } : prev);
    };

    const result = await uploadFileToS3(file);
    if (result) {
      const needsConversion = !file.name.toLowerCase().endsWith('.mp4');
      if (needsConversion) {
        // Non-MP4 files (MOV, WebM, AVI, MKV) need MediaConvert conversion to normalized MP4
        setVideo(prev => prev ? { ...prev, s3Key: result.key, s3Url: result.downloadUrl, status: 'converting' as const } : prev);
        convertMovClip(result.key);
      } else {
        setVideo(prev => prev ? { ...prev, s3Key: result.key, s3Url: result.downloadUrl, status: 'uploaded' as const } : prev);
      }
    }
  }, [getAuthToken, getAuthHeaders, isSubscriber, subscriptionLoading, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'] },
    multiple: false,
  });

  // "Choose from your library" — a staged library file slots in exactly like a
  // completed upload (the staged key already lives in the ephemeral tool bucket).
  const openLibraryPicker = () => {
    if (!getAuthToken()) { setShowLoginModal(true); return; }
    if (!subscriptionLoading && !isSubscriber) { setShowUpgradeModal(true); return; }
    setShowLibraryPicker(true);
  };

  const handleLibraryPick = async (f: StagedLibraryFile) => {
    const fileName = stagedFileName(f);
    // Replacing the source invalidates everything downstream (mirrors onDrop)
    setVideo(prev => { if (prev) URL.revokeObjectURL(prev.objectUrl); return prev; });
    setRenderStatus('idle');
    setRenderProgress(0);
    setRenderError('');
    setFinalVideoUrl('');
    setCompletedSteps(new Set());

    const base: VideoInfo = {
      file: new File([], fileName, { type: 'video/mp4' }),
      name: f.name,
      objectUrl: f.downloadUrl,
      s3Key: f.key,
      s3Url: f.downloadUrl,
      duration: 0,
      width: 0,
      height: 0,
      status: 'uploaded',
    };
    if (!fileName.toLowerCase().endsWith('.mp4')) {
      // Non-MP4 library files go through the same MediaConvert path as uploads
      setVideo({ ...base, status: 'converting' });
      convertMovClip(f.key);
      return;
    }
    setVideo(base);
    try {
      const meta = await probeVideoUrl(f.downloadUrl);
      setVideo(prev => prev ? { ...prev, duration: meta.duration, width: meta.width, height: meta.height } : prev);
    } catch { /* Shotstack sizing falls back gracefully */ }
  };

  const videoReady = !!video && video.status === 'uploaded' && video.duration > 0;

  // ===== SHOTSTACK OUTPUT CONFIG (from single source video) =====
  const getSourceDimensions = (): { width: number; height: number } => {
    if (video?.width && video?.height) return { width: video.width, height: video.height };
    return { width: 1920, height: 1080 };
  };

  // Detect aspect ratio category from dimensions
  const getAspectRatioCategory = (): '16:9' | '9:16' | '1:1' | '4:3' | '3:4' | 'custom' => {
    const { width, height } = getSourceDimensions();
    const ratio = width / height;
    if (Math.abs(ratio - 1) < 0.05) return '1:1';
    if (Math.abs(ratio - 16 / 9) < 0.1) return '16:9';
    if (Math.abs(ratio - 9 / 16) < 0.1) return '9:16';
    if (Math.abs(ratio - 4 / 3) < 0.1) return '4:3';
    if (Math.abs(ratio - 3 / 4) < 0.1) return '3:4';
    return 'custom';
  };

  // Get Shotstack output config that preserves source aspect ratio
  const getShotstackOutputConfig = () => {
    const ar = getAspectRatioCategory();
    const { width, height } = getSourceDimensions();
    // For standard 16:9, use Shotstack's built-in 'hd' resolution
    if (ar === '16:9') return { format: 'mp4', resolution: 'hd', fps: 30, quality: 'high' };
    // For all other aspect ratios, preserve source dimensions exactly when they
    // already fit inside a 1920×1080 box. Only scale down when source is bigger.
    let outW = width;
    let outH = height;
    const maxW = 1920;
    const maxH = 1080;
    if (outW > maxW || outH > maxH) {
      const scale = Math.min(maxW / outW, maxH / outH);
      outW = Math.round(outW * scale);
      outH = Math.round(outH * scale);
    }
    outW = outW % 2 === 0 ? outW : outW + 1;
    outH = outH % 2 === 0 ? outH : outH + 1;
    return { format: 'mp4', size: { width: outW, height: outH }, fps: 30, quality: 'high' };
  };

  // ===== SHOTSTACK JSON =====
  const buildShotstackJson = (): any => {
    const sourceVideoUrl = video!.s3Url;
    const effectiveDuration = video!.duration;
    const tracks: any[] = [];
    const slateDuration = 3; // black screen duration
    const slateOnBlack = nameSlate.timing !== 'overlay';
    const videoStart = slateOnBlack ? slateDuration : 0;

    const videoClip: any = { asset: { type: 'video', src: sourceVideoUrl, volume: 1 }, start: videoStart, length: effectiveDuration };
    tracks.push({ clips: [videoClip] });

    // Name slate
    const timelineFonts: any[] = [];
    const slateHtml = [nameSlate.name.toUpperCase(), nameSlate.role || null, nameSlate.agency || null].filter(Boolean).join('<br>');
    const font = nameSlate.font || 'Montserrat';
    const pos = nameSlate.position || 'center';

    // Add font URL to timeline if we have it
    const fontUrl = FONT_URLS[font];
    if (fontUrl) {
      timelineFonts.push({ src: fontUrl });
    }

    // Build slate clip — use CSS for vertical placement since HTML assets fill the viewport
    const buildSlateClip = (start: number, length: number, fontSize: number, fadeIn: boolean) => {
      const verticalCss = pos === 'bottom'
        ? 'display: flex; align-items: flex-end; justify-content: center; width: 100%; height: 100%; padding-bottom: 8%;'
        : 'display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;';
      const clip: any = {
        asset: {
          type: 'html',
          html: `<div><p>${slateHtml}</p></div>`,
          css: `div { ${verticalCss} } p { font-family: '${font}'; font-size: ${fontSize}px; color: #ffffff; text-align: center; text-shadow: 1px 1px 4px rgba(0,0,0,0.8); }`
        },
        start,
        length,
        transition: fadeIn ? { in: 'fade', out: 'fade' } : { out: 'fade' },
      };
      return clip;
    };

    if (slateOnBlack) {
      // Option A: Text over black screen at the beginning
      tracks.unshift({ clips: [buildSlateClip(0, slateDuration, 32, false)] });
      // Black screen: bottom track
      tracks.push({
        clips: [{
          asset: {
            type: 'html',
            html: '<div></div>',
            css: 'div { width: 100%; height: 100%; background-color: #000000; }',
          },
          start: 0,
          length: slateDuration,
        }]
      });
    } else {
      // Option B: Text overlaid on the video for the first N seconds
      const fontSize = pos === 'bottom' ? 24 : 32;
      tracks.unshift({ clips: [buildSlateClip(0, slateDuration, fontSize, true)] });
    }

    const timeline: any = { tracks };
    if (timelineFonts.length > 0) {
      timeline.fonts = timelineFonts;
    }
    const json = { timeline, output: getShotstackOutputConfig() };
    console.log('[NameSlate] Shotstack JSON:', JSON.stringify(json, null, 2));
    return json;
  };

  // ===== STEP 3: RENDER =====
  const startRender = () => {
    if (!video || !videoReady) {
      toast({ title: 'No video', description: 'Upload a video first.', variant: 'destructive' });
      return;
    }
    if (!nameSlate.name) {
      toast({ title: 'Name required', description: 'Enter a name for the slate.', variant: 'destructive' });
      return;
    }

    const runRender = async () => {
      setRenderStatus('rendering');
      setRenderProgress(5);
      setRenderError('');
      try {
        const authToken = getAuthToken();
        const json = buildShotstackJson();
        const renderRes = await fetch('/api/shotstack/render', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}) },
          body: JSON.stringify({ json }),
        });
        if (!renderRes.ok) {
          const err = await renderRes.json().catch(() => ({}));
          throw new Error(err.error || err.details || 'Render submission failed');
        }
        const renderData = await renderRes.json();
        toast({ title: 'Rendering video...' });
        pollRenderStatus(renderData.id);
      } catch (err: any) {
        setRenderStatus('failed');
        setRenderError(err.message);
        toast({ title: 'Render failed', description: err.message, variant: 'destructive' });
      }
    };

    requireCreditConfirmation(CREDIT_OPERATIONS.SHOTSTACK_RENDER, 'Video Render', runRender);
  };

  const pollRenderStatus = (id: string) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`/api/shotstack/render-status/${id}`);
        const data = await res.json();
        const doneUrl = data.videoUrl || data.url;
        if (data.status === 'done' && doneUrl) {
          clearInterval(interval);
          setRenderProgress(100);
          finishRender(doneUrl);
        } else if (data.status === 'failed') {
          clearInterval(interval);
          const errMsg = data.errorDetails?.message || data.error || data.message || 'Rendering failed.';
          console.error('[NameSlate] Render failed:', data);
          setRenderStatus('failed');
          setRenderError(errMsg);
          toast({ title: 'Render failed', description: errMsg, variant: 'destructive' });
        } else {
          if (data.status === 'fetching') setRenderProgress(10);
          else if (data.status === 'queued') setRenderProgress(20);
          else if (data.status === 'rendering') setRenderProgress(prev => Math.min(90, prev + 5));
          else if (data.status === 'saving') setRenderProgress(95);
        }
        if (attempts >= 360) {
          clearInterval(interval);
          setRenderStatus('failed');
          toast({ title: 'Render timed out', variant: 'destructive' });
        }
      } catch { if (attempts >= 360) { clearInterval(interval); setRenderStatus('failed'); } }
    }, 5000);
  };

  const finishRender = (url: string) => {
    setFinalVideoUrl(url);
    setRenderStatus('completed');
    setCompletedSteps(prev => new Set([...prev, 'process' as WizardStep]));
    setCurrentStep('download');
  };

  // ===== STEP 4: DOWNLOAD =====
  const getDownloadFileName = () => {
    if (!video) return 'slated_video.mp4';
    const baseName = video.name.replace(/\.[^.]+$/, ''); // Strip original extension
    return `slated_${baseName}.mp4`;
  };

  const handleDownload = async () => {
    const fileName = getDownloadFileName();
    const isIOS = /iPhone|iPad/i.test(navigator.userAgent);

    // iOS: use Web Share API so user gets "Save Video" option for camera roll
    if (isIOS && navigator.share) {
      try {
        // Fetch as blob and share as file — this gives "Save Video" in share sheet
        const resp = await fetch(finalVideoUrl);
        if (resp.ok) {
          const blob = await resp.blob();
          const file = new File([blob], fileName, { type: 'video/mp4' });
          if (navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], title: fileName });
            return;
          }
        }
      } catch {
        // Share cancelled or failed — fall through to URL-based share
      }

      // Fallback: share the URL (still gives Save Video on iOS 16+)
      try {
        await navigator.share({ url: finalVideoUrl, title: fileName });
        return;
      } catch {
        // User cancelled — fall through to direct download
      }
    }

    // Determine the fetch URL — proxy URLs get download=1 for Content-Disposition
    let fetchUrl = finalVideoUrl;
    if (finalVideoUrl.includes('/api/s3/')) {
      const separator = finalVideoUrl.includes('?') ? '&' : '?';
      fetchUrl = `${finalVideoUrl}${separator}download=1&filename=${encodeURIComponent(fileName)}`;
    }

    // Desktop / Android: fetch blob and trigger download
    try {
      const resp = await fetch(fetchUrl);
      if (!resp.ok) throw new Error(`${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    } catch {
      // Fetch failed (CORS or 404) — fall through to direct navigation
    }

    // Fallback: direct navigation
    if (finalVideoUrl.includes('/api/s3/')) {
      window.location.href = fetchUrl;
    } else {
      window.open(finalVideoUrl, '_blank');
    }
  };

  // ===== STEP RENDERERS =====
  const renderUploadStep = () => {
    const isUploading = video?.status === 'uploading';
    const isConverting = video?.status === 'converting';
    const isError = video?.status === 'error';
    return (
      <StepCard>
        <StepHeader
          kicker="Step 01 · Upload"
          title="Drop the tape."
          subtitle="One video — we'll burn your slate onto it. MOV files convert automatically."
        />

        {/* Drop card */}
        <div
          {...getRootProps()}
          className="flex flex-col justify-between cursor-pointer transition-colors"
          style={{
            padding: '18px 20px',
            minHeight: 140,
            borderRadius: 'var(--hj-r-md)',
            border: `1.5px dashed ${isDragActive ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
            background: isDragActive ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
            gap: 14,
          }}
        >
          <input {...getInputProps()} />
          <div className="flex items-center justify-between gap-3">
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 40,
                height: 40,
                borderRadius: 'var(--hj-r-sm)',
                background: 'var(--hj-surface-3)',
                color: 'var(--hj-ink-2)',
              }}
            >
              <Upload size={18} strokeWidth={1.8} />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--hj-ink)', marginBottom: 2 }}>
              Drop or browse a clip
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.4 }}>
              MP4 · MOV · WebM · MKV — one file.
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={openLibraryPicker}
          className="inline-flex items-center gap-2"
          style={{
            marginTop: 10,
            padding: '8px 12px',
            borderRadius: 'var(--hj-r-sm)',
            border: '1px solid var(--hj-hairline)',
            background: 'transparent',
            color: 'var(--hj-ink-2)',
            fontSize: 12.5,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          <FolderOpen size={14} strokeWidth={1.8} />
          Choose from your library
        </button>

        {/* Uploaded file row */}
        {video && (
          <div style={{ marginTop: 18 }}>
            <div className="mono-label" style={{ marginBottom: 10 }}>Your clip</div>
            <div
              className="flex items-center"
              style={{
                gap: 14,
                padding: '10px 12px',
                borderRadius: 'var(--hj-r-sm)',
                background: 'var(--hj-surface-2)',
                border: '1px solid var(--hj-hairline-soft)',
              }}
            >
              <div
                className="flex items-center justify-center flex-shrink-0 relative overflow-hidden"
                style={{
                  width: 76,
                  height: 44,
                  borderRadius: 4,
                  background: 'oklch(0.12 0.005 70)',
                  color: 'var(--hj-ink-4)',
                }}
              >
                <Video size={16} strokeWidth={1.5} />
                {video.duration > 0 && (
                  <span
                    className="tabular"
                    style={{
                      position: 'absolute',
                      right: 3,
                      bottom: 3,
                      padding: '1px 5px',
                      borderRadius: 2,
                      background: 'oklch(0 0 0 / 0.6)',
                      color: 'var(--hj-ink)',
                      fontFamily: 'var(--hj-font-mono)',
                      fontSize: 9,
                    }}
                  >
                    {formatTime(video.duration)}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="truncate"
                  style={{ fontSize: 13.5, color: 'var(--hj-ink)', fontWeight: 500 }}
                >
                  {video.name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--hj-ink-3)', marginTop: 2 }}>
                  {isUploading
                    ? `Uploading… ${video.uploadProgress ?? 0}%`
                    : isConverting
                      ? 'Converting MOV → MP4'
                      : isError
                        ? 'Upload error'
                        : video.width && video.height
                          ? `Uploaded · ${video.width}×${video.height}`
                          : 'Uploaded'}
                </div>
                {isUploading && (
                  <Progress value={video.uploadProgress ?? 0} className="h-1 mt-1.5" />
                )}
              </div>
              {isUploading && <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />}
              {isConverting && <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />}
              {video.status === 'uploaded' && <CheckCircle className="w-4 h-4" style={{ color: 'var(--hj-yellow-ink)' }} />}
              {isError && <AlertCircle className="w-4 h-4 text-destructive" />}
              <button
                type="button"
                onClick={() => { URL.revokeObjectURL(video.objectUrl); setVideo(null); setCompletedSteps(new Set()); }}
                aria-label="Remove clip"
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  background: 'transparent',
                  border: '1px solid var(--hj-hairline-soft)',
                  color: 'var(--hj-ink-3)',
                  cursor: 'pointer',
                }}
              >
                <X size={13} strokeWidth={1.8} />
              </button>
            </div>

            {/* Preview */}
            {video.status === 'uploaded' && (
              <div
                className="aspect-video overflow-hidden relative"
                style={{
                  background: 'oklch(0.08 0.005 70)',
                  borderRadius: 'var(--hj-r-md)',
                  border: '1px solid var(--hj-hairline)',
                  marginTop: 14,
                }}
              >
                <video
                  src={video.objectUrl}
                  className="w-full h-full object-contain"
                  controls
                  preload="metadata"
                  playsInline
                />
              </div>
            )}
          </div>
        )}
      </StepCard>
    );
  };

  const renderSlateStep = () => {
    const fields: Array<{ key: 'name' | 'role' | 'agency'; label: string; placeholder: string }> = [
      { key: 'name',   label: 'Name',          placeholder: 'Jane Doe' },
      { key: 'role',   label: 'Role / Project', placeholder: 'Commercial — Nike' },
      { key: 'agency', label: 'Agency',         placeholder: 'WME' },
    ];
    const fontOptions = ['Montserrat', 'Open Sans', 'Lato', 'Roboto', 'Playfair Display', 'Oswald', 'Raleway', 'Poppins'];
    return (
      <StepCard>
        <StepHeader
          kicker="Step 02 · Slate"
          title="Name slate."
          subtitle="Burn the basics — name, role, agency — to the first frame or as a lower-third overlay."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          {fields.map((f) => (
            <label key={f.key} className="flex flex-col gap-1.5">
              <span className="mono-label">{f.label}</span>
              <Input
                value={nameSlate[f.key]}
                onChange={(e) =>
                  setNameSlate((prev) => ({ ...prev, [f.key]: e.target.value }))
                }
                placeholder={f.placeholder}
              />
            </label>
          ))}
          <label className="flex flex-col gap-1.5">
            <span className="mono-label">Font</span>
            <Select
              value={nameSlate.font}
              onValueChange={(v) => setNameSlate((prev) => ({ ...prev, font: v }))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {fontOptions.map((f) => (
                  <SelectItem key={f} value={f}>
                    <span style={{ fontFamily: `${f}, sans-serif` }}>{f}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </div>

        {/* Position + Timing as a segmented control row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5" style={{ marginTop: 14 }}>
          <div>
            <div className="mono-label" style={{ marginBottom: 6 }}>Position</div>
            <SegmentedControl
              options={[
                { value: 'center', label: 'First frame' },
                { value: 'bottom', label: 'Lower third' },
              ]}
              active={nameSlate.position}
              onChange={(v) =>
                setNameSlate((prev) => ({ ...prev, position: v as 'center' | 'bottom' }))
              }
            />
          </div>
          <div>
            <div className="mono-label" style={{ marginBottom: 6 }}>Timing</div>
            <SegmentedControl
              options={[
                { value: 'black',   label: 'Over black' },
                { value: 'overlay', label: 'Over video' },
              ]}
              active={nameSlate.timing}
              onChange={(v) =>
                setNameSlate((prev) => ({ ...prev, timing: v as 'black' | 'overlay' }))
              }
            />
          </div>
        </div>

        {/* Preview */}
        {nameSlate.name && (
          <div
            className="text-center flex flex-col"
            style={{
              marginTop: 18,
              padding: 16,
              borderRadius: 'var(--hj-r-md)',
              border:
                nameSlate.timing === 'overlay'
                  ? '1px solid var(--hj-hairline)'
                  : 'none',
              background:
                nameSlate.timing === 'overlay'
                  ? 'var(--hj-surface-2)'
                  : 'oklch(0.08 0.005 70)',
              justifyContent: nameSlate.position === 'bottom' ? 'flex-end' : 'center',
              minHeight: 120,
            }}
          >
            <div>
              <p
                style={{
                  fontFamily: `${nameSlate.font}, sans-serif`,
                  fontSize: 18,
                  fontWeight: 600,
                  color:
                    nameSlate.timing === 'overlay'
                      ? 'var(--hj-ink)'
                      : 'white',
                  textShadow:
                    nameSlate.timing === 'overlay'
                      ? '1px 1px 4px oklch(0 0 0 / 0.8)'
                      : '1px 1px 3px oklch(0 0 0 / 0.7)',
                  margin: 0,
                }}
              >
                {nameSlate.name.toUpperCase()}
              </p>
              {nameSlate.role && (
                <p
                  style={{
                    fontFamily: `${nameSlate.font}, sans-serif`,
                    fontSize: 13.5,
                    color:
                      nameSlate.timing === 'overlay'
                        ? 'var(--hj-ink-2)'
                        : 'oklch(1 0 0 / 0.9)',
                    margin: '4px 0 0',
                  }}
                >
                  {nameSlate.role}
                </p>
              )}
              {nameSlate.agency && (
                <p
                  style={{
                    fontFamily: `${nameSlate.font}, sans-serif`,
                    fontSize: 13,
                    color:
                      nameSlate.timing === 'overlay'
                        ? 'var(--hj-ink-3)'
                        : 'oklch(1 0 0 / 0.8)',
                    margin: '2px 0 0',
                  }}
                >
                  {nameSlate.agency}
                </p>
              )}
            </div>
            <p className="mono-label" style={{ marginTop: 10, fontSize: 9, opacity: 0.7 }}>
              Shown {nameSlate.timing === 'overlay' ? 'over video' : 'over black'} for 3s
            </p>
          </div>
        )}
      </StepCard>
    );
  };

  const renderProcessStep = () => {
    const inFlight = renderStatus === 'rendering';
    const summaryRows: { label: string; value: string }[] = [
      { label: 'Clip',     value: video ? video.name : '—' },
      { label: 'Slate',    value: nameSlate.name ? nameSlate.name.toUpperCase() : '—' },
      { label: 'Position', value: nameSlate.position === 'bottom' ? 'Lower third' : 'First frame' },
      { label: 'Timing',   value: nameSlate.timing === 'overlay' ? 'Over video' : 'Over black' },
      { label: 'Font',     value: nameSlate.font },
    ];

    return (
      <StepCard>
        <StepHeader
          kicker="Step 03 · Render"
          title="Joe's working."
          subtitle="The slate gets burned in by our render pipeline — usually under a minute."
        />

        {/* Settings summary — always visible. Mono kickers + ink-2 values. */}
        <div
          style={{
            padding: '14px 16px',
            borderRadius: 'var(--hj-r-sm)',
            background: 'var(--hj-surface-2)',
            border: '1px solid var(--hj-hairline-soft)',
            marginBottom: 16,
          }}
        >
          <div className="mono-label" style={{ marginBottom: 8 }}>Final tape</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {summaryRows.map((row) => (
              <div key={row.label} className="flex items-baseline justify-between gap-3">
                <span style={{ fontSize: 12, color: 'var(--hj-ink-4)' }}>{row.label}</span>
                <span
                  className="truncate text-right"
                  style={{ fontSize: 13, color: 'var(--hj-ink-2)', fontWeight: 500 }}
                >
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* In-flight progress card */}
        {inFlight && (
          <div
            style={{
              padding: '20px 22px',
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-2)',
              border: '1px solid var(--hj-hairline-soft)',
              marginBottom: 14,
            }}
          >
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
              <div className="flex items-center gap-2.5">
                <span
                  aria-hidden
                  className="rounded-full"
                  style={{
                    width: 10,
                    height: 10,
                    background: 'var(--hj-blue)',
                    boxShadow: '0 0 12px var(--hj-blue)',
                    animation: 'pulse 1.4s ease-in-out infinite',
                  }}
                />
                <span style={{ fontSize: 14.5, color: 'var(--hj-ink)', fontWeight: 500 }}>
                  Rendering video
                </span>
              </div>
              <span
                className="tabular"
                style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 14, color: 'var(--hj-ink-2)' }}
              >
                {Math.min(100, Math.round(renderProgress))}%
              </span>
            </div>
            <div
              style={{
                height: 6,
                borderRadius: 999,
                background: 'var(--hj-surface-3)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(100, renderProgress)}%`,
                  background: 'var(--hj-blue)',
                  transition: 'width 0.25s ease',
                }}
              />
            </div>
            <div className="flex items-center justify-between" style={{ marginTop: 12 }}>
              <span className="mono-label">Don't close the tab</span>
              <span className="mono-label">1 video token</span>
            </div>
          </div>
        )}

        {renderStatus === 'failed' && (
          <div
            style={{
              padding: 16,
              borderRadius: 'var(--hj-r-sm)',
              border: '1px solid hsl(var(--destructive) / 0.5)',
              background: 'hsl(var(--destructive) / 0.08)',
            }}
          >
            <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
              <AlertCircle className="w-5 h-5" style={{ color: 'hsl(var(--destructive))' }} />
              <p style={{ fontWeight: 600, color: 'hsl(var(--destructive))' }}>Render failed</p>
            </div>
            {renderError && (
              <p style={{ fontSize: 13, color: 'hsl(var(--destructive) / 0.85)', margin: 0, lineHeight: 1.4 }}>
                {renderError}
              </p>
            )}
          </div>
        )}

        {renderStatus === 'completed' && (
          <div
            className="flex items-center gap-3"
            style={{
              padding: '14px 16px',
              borderRadius: 'var(--hj-r-sm)',
              background: 'oklch(0.42 0.15 145 / 0.12)',
              border: '1px solid var(--hj-green)',
              color: 'var(--hj-ink)',
            }}
          >
            <CheckCircle className="w-5 h-5" style={{ color: 'var(--hj-green)' }} />
            <div>
              <div style={{ fontWeight: 600 }}>Render complete</div>
              <div style={{ fontSize: 12, color: 'var(--hj-ink-2)', marginTop: 2 }}>
                Continue to download your slated tape.
              </div>
            </div>
          </div>
        )}
      </StepCard>
    );
  };

  const renderDownloadStep = () => (
    <StepCard>
      <StepHeader
        kicker="Step 04 · Download"
        title="Slate's on."
        subtitle="Save the finished tape to your device, or start over with a new clip."
      />

      {finalVideoUrl ? (
        <>
          {/* Big preview tile */}
          <div
            className="aspect-video overflow-hidden relative"
            style={{
              background: 'oklch(0.08 0.005 70)',
              borderRadius: 'var(--hj-r-md)',
              border: '1px solid var(--hj-hairline)',
              marginBottom: 16,
            }}
          >
            <video
              ref={previewVideoRef}
              src={finalVideoUrl}
              className="w-full h-full object-contain"
              controls
              preload="auto"
              playsInline
            />
          </div>

          {/* Primary action row */}
          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center justify-center gap-2 transition-opacity"
              style={{
                padding: '12px 22px',
                borderRadius: 999,
                background: 'var(--hj-blue)',
                border: '1px solid var(--hj-blue)',
                color: 'oklch(0.18 0.005 70)',
                fontSize: 15,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 48,
              }}
            >
              <Download className="w-4 h-4" />
              Save to device
            </button>
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center justify-center gap-2"
              style={{
                padding: '12px 22px',
                borderRadius: 999,
                background: 'transparent',
                border: '1px solid var(--hj-hairline)',
                color: 'var(--hj-ink-2)',
                fontSize: 15,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 48,
              }}
            >
              <RotateCcw className="w-4 h-4" />
              Start over
            </button>
          </div>
        </>
      ) : (
        <div
          className="flex items-center gap-3"
          style={{
            padding: '14px 16px',
            borderRadius: 'var(--hj-r-sm)',
            background: 'var(--hj-surface-2)',
            border: '1px solid var(--hj-hairline-soft)',
            color: 'var(--hj-ink-2)',
            fontSize: 13.5,
          }}
        >
          <Monitor className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--hj-ink-4)' }} />
          No finished video yet — render the slate first.
        </div>
      )}
    </StepCard>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'upload': return renderUploadStep();
      case 'slate': return renderSlateStep();
      case 'process': return renderProcessStep();
      case 'download': return renderDownloadStep();
      default: return null;
    }
  };

  // Per-step shell-level Next behavior — the toolbar and MobileActionBar Next
  // buttons do the same thing as the step's primary action.
  const currentStepAdvance: {
    onAdvance: () => void;
    label?: string;
    disabled?: boolean;
    showAdvance?: boolean;
  } = (() => {
    switch (currentStep) {
      case 'upload':
        return {
          onAdvance: advanceStep,
          label: video?.status === 'converting' ? 'Converting…' : 'Continue to Slate',
          disabled: !videoReady,
        };
      case 'slate':
        return {
          onAdvance: advanceStep,
          label: 'Continue to Render',
          disabled: !nameSlate.name.trim(),
        };
      case 'process': {
        if (renderStatus === 'idle') {
          return { onAdvance: () => startRender(), label: 'Render video · 1 video token' };
        }
        if (renderStatus === 'completed') {
          return { onAdvance: advanceStep, label: 'Continue to Download' };
        }
        if (renderStatus === 'failed') {
          return {
            onAdvance: () => { setRenderStatus('idle'); setRenderError(''); },
            label: 'Try again',
          };
        }
        return { onAdvance: () => {}, label: 'Rendering…', disabled: true };
      }
      case 'download':
        // Terminal step — workspace owns its actions (Save to Device, Start
        // Over). Hide the shell-level Next.
        return { onAdvance: () => {}, showAdvance: false };
      default:
        return { onAdvance: advanceStep };
    }
  })();

  const onShellBack = stepIndex > 0 ? () => goToStep(STEP_ORDER[stepIndex - 1]) : undefined;

  return (
    <div className="min-h-screen" style={{ background: 'var(--hj-bg)' }}>
      <ToolShell
        toolName="Name Slate"
        toolNumber={`Tool · ${String(stepIndex + 1).padStart(2, '0')} / ${String(STEPS.length).padStart(2, '0')}`}
        usesCredits
        steps={STEPS}
        currentStepKey={currentStep}
        completedKeys={completedSteps as Set<string>}
        onStepChange={(k) => goToStep(k as WizardStep)}
        onBack={onShellBack}
        onAdvance={currentStepAdvance.onAdvance}
        advanceLabel={currentStepAdvance.label}
        advanceDisabled={currentStepAdvance.disabled}
        showAdvance={currentStepAdvance.showAdvance !== false}
        onBackToStudio={() => navigate('/')}
      >
        {renderCurrentStep()}
      </ToolShell>

      <CreditConfirmDialog />
      <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} trigger="ai_feature" />
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <LibraryPicker open={showLibraryPicker} onClose={() => setShowLibraryPicker(false)} onPick={handleLibraryPick} />
    </div>
  );
};
